import { TAILLE_PAGE, decalage as calculerDecalage } from './pagination'
import { preparerTerme } from './recherche'
import { supabase } from './supabase'

/**
 * Acces a la liste des dossiers.
 *
 * Tout passe par `public.rechercher_dossiers` (migration 0006) : le tri, le
 * filtre des archives, l'echappement du terme et le plafond de pagination sont
 * en base, au meme endroit pour tous les appelants. Le navigateur ne construit
 * aucun motif.
 */

export interface LigneDossier {
  readonly id: string
  readonly nom: string
  readonly prenom: string
  readonly cree_le: string
  /** Affichage seul : astérisque après le nom. Jamais recopié dans nom ni prenom. */
  readonly homonyme: boolean
}

export interface PageDossiers {
  readonly lignes: readonly LigneDossier[]
  /** Nombre total de dossiers correspondants, avant pagination. */
  readonly total: number
}

export class ErreurRecherche extends Error {
  override readonly cause: unknown

  constructor(message: string, cause?: unknown) {
    super(message)
    this.name = 'ErreurRecherche'
    this.cause = cause
  }
}

interface LigneRpc extends LigneDossier {
  readonly total: number
}

export async function rechercherDossiers(terme: string, page: number): Promise<PageDossiers> {
  const { data, error } = await supabase.rpc('rechercher_dossiers', {
    terme: preparerTerme(terme),
    limite: TAILLE_PAGE,
    decalage: calculerDecalage(page),
  })

  if (error) {
    // L'erreur remonte : « la requete a echoue » et « aucun resultat » sont deux
    // etats distincts a l'ecran, et les confondre ferait passer une panne pour
    // une base vide.
    throw new ErreurRecherche(error.message, error)
  }

  const lignes = (data ?? []) as LigneRpc[]
  const premiere = lignes[0]

  return {
    lignes: lignes.map(({ id, nom, prenom, cree_le, homonyme }) => ({
      id,
      nom,
      prenom,
      cree_le,
      // Une absence de colonne (base pas encore migree) vaut « pas homonyme ».
      homonyme: homonyme === true,
    })),
    // Le total accompagne chaque ligne ; une page vide n'en porte aucune.
    total: premiere ? premiere.total : 0,
  }
}
